import { useEffect, useMemo, useState } from "react";
import {
  ArrowLeft,
  CalendarDays,
  CarFront,
  ClipboardList,
  Clock,
  MapPin,
  Receipt,
  Shield,
  User,
  Users,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import bookingService from "../../../api/services/bookingService";
import AppSelect from "../../../components/AppSelect";
import { useNavigate } from "react-router-dom";
import { useToast } from "../../../hooks/useToast";
import { mapTripStatus } from "../../../utils/tripStatus";
import {
  formatBookingDateTime,
  formatBookingDuration,
} from "../../../utils/bookingWindow";

const PAGE_SIZE = 5;
const FILTER_OPTIONS = [
  "Most Recent",
  "Active",
  "Unconfirmed",
  "Completed",
  "Upcoming",
  "Pending",
];

const statusStyles = {
  Upcoming: "bg-[#F6F0F9] text-[#9B5DE5]",
  Active: "bg-[#EAFBF1] text-[#16A34A]",
  // A trip whose dates have passed but whose confirmations never landed. Not
  // completed, and not finished with: her money is still held.
  Unconfirmed: "bg-[#FFF6E5] text-[#B26700]",
  Completed: "bg-[#F8EEF4] text-[#B5478C]",
  Pending: "bg-[#FFF6E5] text-[#B26700]",
  Cancelled: "bg-[#FDECEC] text-[#D92D20]",
};

const toNumber = (value) => {
  if (value === null || value === undefined) return 0;
  if (typeof value === "number") return Number.isFinite(value) ? value : 0;
  const cleaned = String(value).replace(/[^0-9.-]/g, "");
  if (!cleaned) return 0;
  const parsed = Number(cleaned);
  return Number.isFinite(parsed) ? parsed : 0;
};

const parseDateValue = (value) => {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatDate = (value) => {
  if (!value) return "—";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  return parsed.toLocaleDateString("en-GB");
};

const formatCurrency = (value, currency = "₦") =>
  `${currency}${toNumber(value).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const resolveDurationDays = (item = {}, startDate) => {
  const directDays =
    item?.durationDays ??
    item?.duration_days ??
    item?.numberOfDays ??
    item?.number_of_days ??
    item?.days;

  const directNumber = toNumber(directDays);
  if (directNumber > 0) return directNumber;

  const endDate = parseDateValue(
    item?.endDate ||
      item?.end_date ||
      item?.departureDate ||
      item?.departure_date ||
      item?.toDate ||
      item?.to_date
  );

  if (startDate && endDate) {
    const start = new Date(startDate);
    const end = new Date(endDate);
    start.setHours(0, 0, 0, 0);
    end.setHours(0, 0, 0, 0);
    const diffInDays = Math.round((end.getTime() - start.getTime()) / 86400000);
    return Math.max(1, diffInDays + 1);
  }

  return 1;
};

const normalizeTrip = (item = {}, index = 0) => {
  const startDate =
    item?.arrivalDate ||
    item?.date ||
    item?.startDate ||
    item?.start_date ||
    item?.scheduledAt ||
    item?.scheduled_at ||
    item?.createdAt ||
    item?.created_at ||
    "";

  const amount =
    item?.amountExpected ||
    item?.amountPaid ||
    item?.amount ||
    item?.totalAmount ||
    item?.total_amount ||
    item?.price ||
    item?.paidAmount ||
    item?.paid_amount ||
    0;

  const participants =
    item?.touristsCount ||
    item?.participants ||
    item?.participantsCount ||
    item?.participants_count ||
    item?.guests ||
    item?.guestCount ||
    item?.guest_count ||
    0;

  return {
    id:
      item?._id ||
      item?.id ||
      item?.bookingId ||
      item?.booking_id ||
      item?.reference ||
      `trip-${index}`,
    location:
      item?.tour?.title ||
      item?.tour?.name ||
      item?.service?.name ||
      item?.service?.title ||
      item?.experience ||
      item?.title ||
      item?.location ||
      "—",
    date: startDate,
    dateLabel: formatDate(startDate),
    checkOut:
      item?.departureDate ||
      item?.endDate ||
      item?.end_date ||
      item?.checkOutDate ||
      item?.check_out ||
      item?.toDate ||
      item?.to_date ||
      startDate,
    checkOutLabel: formatDate(
      item?.departureDate ||
        item?.endDate ||
        item?.end_date ||
        item?.checkOutDate ||
        item?.check_out ||
        item?.toDate ||
        item?.to_date ||
        startDate
    ),
    numberOfDays: resolveDurationDays(item, parseDateValue(startDate)),
    participants: toNumber(participants),
    amountLabel: formatCurrency(amount),
    status: mapTripStatus(
      item?.status,
      startDate,
      item?.departureDate || item?.endDate || item?.end_date || startDate
    ),
    rawStatus: item?.status || "",
    rawItem: item,
  };
};

const normalizeTripsPayload = (payload) => {
  const source = payload?.data ?? payload ?? {};
  const items =
    source?.items ||
    source?.results ||
    source?.bookings ||
    source?.data ||
    source?.rows ||
    [];

  if (!Array.isArray(items)) return [];

  return items.map((item, index) => normalizeTrip(item, index));
};

const resolveCustomerName = (item = {}) => {
  const customer =
    item?.user || item?.customer || item?.client || item?.guest || item?.traveler || {};

  return (
    customer?.name ||
    customer?.fullName ||
    [customer?.firstName, customer?.lastName].filter(Boolean).join(" ").trim() ||
    item?.customerName ||
    item?.guestName ||
    "—"
  );
};

const formatLogisticsValue = (value) => {
  if (!value) return "—";

  if (typeof value === "string" || typeof value === "number") {
    const normalized = String(value).trim();
    return normalized || "—";
  }

  if (Array.isArray(value)) {
    const normalized = value
      .map((item) => {
        if (!item) return "";
        if (typeof item === "string" || typeof item === "number") {
          return String(item).trim();
        }
        const label =
          item?.label ||
          item?.name ||
          item?.type ||
          item?.vehicle ||
          item?.title ||
          "";
        const quantity = item?.quantity ?? item?.count ?? item?.total ?? "";
        if (label && quantity) return `${quantity} ${label}`;
        return label || String(quantity || "").trim();
      })
      .filter(Boolean);

    return normalized.length ? normalized.join(", ") : "—";
  }

  if (typeof value === "object") {
    const normalized = Object.entries(value)
      .map(([key, raw]) => {
        if (raw === null || raw === undefined || raw === "") return "";
        if (typeof raw === "number" || /^[0-9]+$/.test(String(raw).trim())) {
          return `${raw} ${key.replace(/[_-]+/g, " ")}`;
        }
        return `${key.replace(/[_-]+/g, " ")}: ${raw}`;
      })
      .filter(Boolean);

    return normalized.length ? normalized.join(", ") : "—";
  }

  return "—";
};

const sumAddOnSubtotals = (items = []) =>
  Array.isArray(items)
    ? items.reduce((sum, item) => sum + toNumber(item?.subtotal), 0)
    : 0;

const normalizeTripDetail = (payload, fallbackTrip) => {
  const source = payload?.data ?? payload ?? {};
  const booking = source?.booking || source?.data || source || {};
  const item =
    booking && typeof booking === "object" && Object.keys(booking).length
      ? booking
      : fallbackTrip?.rawItem || {};

  const location =
    item?.tour?.title ||
    item?.tour?.name ||
    item?.service?.name ||
    item?.service?.title ||
    item?.experience ||
    item?.title ||
    item?.location ||
    fallbackTrip?.location ||
    "—";

  const date =
    item?.arrivalDate ||
    item?.date ||
    item?.startDate ||
    item?.start_date ||
    item?.scheduledAt ||
    item?.scheduled_at ||
    fallbackTrip?.date ||
    "";

  const checkOut =
    item?.departureDate ||
    item?.endDate ||
    item?.end_date ||
    item?.checkOutDate ||
    item?.check_out ||
    item?.toDate ||
    item?.to_date ||
    fallbackTrip?.checkOut ||
    date;

  const securityOfficers =
    item?.securityOfficers ||
    item?.security_officers ||
    item?.securityPersonnel ||
    item?.security_personnel ||
    item?.securityService ||
    item?.security_service ||
    item?.logistics?.securityOfficers ||
    item?.logistics?.securityPersonnel ||
    item?.logistics?.security ||
    item?.addons?.security ||
    null;

  const convoyVehicles =
    item?.escortVehicles ||
    item?.convoyVehicles ||
    item?.convoy_vehicles ||
    item?.vehicles ||
    item?.vehicleTypes ||
    item?.logistics?.convoyVehicles ||
    item?.logistics?.vehicles ||
    item?.addons?.vehicles ||
    null;

  const vehicleCost = sumAddOnSubtotals(item?.escortVehicles);
  const securityCost = sumAddOnSubtotals(item?.securityPersonnel);
  const total =
    toNumber(item?.amountExpected) ||
    toNumber(item?.amountPaid) ||
    toNumber(fallbackTrip?.rawItem?.amountExpected) ||
    toNumber(fallbackTrip?.rawItem?.amountPaid);
  const baseTripCost = Math.max(0, total - vehicleCost - securityCost);

  return {
    id:
      item?._id ||
      fallbackTrip?.id ||
      item?.id ||
      item?.bookingId ||
      item?.booking_id ||
      "—",
    bookingId:
      item?._id ||
      item?.id ||
      item?.bookingId ||
      item?.booking_id ||
      item?.reference ||
      fallbackTrip?.bookingId ||
      "—",
    customerName: resolveCustomerName(item),
    participants:
      toNumber(
        item?.touristsCount ||
          item?.participants ||
          item?.participantsCount ||
          item?.participants_count ||
          item?.guests ||
          item?.guestCount ||
          item?.guest_count
      ) ||
      fallbackTrip?.participants ||
      0,
    location,
    // Dates alone left a 12-hour block ambiguous: the assistant could see the
    // day she was booked for but not the hour she had to be there.
    checkInLabel: formatBookingDateTime(date),
    checkOutLabel: formatBookingDateTime(checkOut),
    durationLabel: formatBookingDuration(item),
    bookedOnLabel: formatBookingDateTime(item?.createdAt),
    reference:
      item?.paymentReference || item?._id || fallbackTrip?.bookingId || "—",
    itinerary: item?.itinerary || fallbackTrip?.rawItem?.itinerary || "",
    securityOfficersLabel: formatLogisticsValue(securityOfficers),
    convoyVehiclesLabel: formatLogisticsValue(convoyVehicles),
    baseTripCostLabel: formatCurrency(baseTripCost),
    vehicleCostLabel: formatCurrency(vehicleCost),
    securityCostLabel: formatCurrency(securityCost),
    totalLabel: formatCurrency(total),
    status: fallbackTrip?.status || mapTripStatus(item?.status, date, checkOut),
  };
};

// Telling the traveller she is on site. This is her own signal, not part of the
// money handshake — the server takes it only while the booking is still
// `accepted`, and the start confirmation does not depend on it.
const getArrivalAction = (trip) => {
  const raw = String(trip?.rawStatus || "").trim().toLowerCase();
  if (raw === "assistant_arrived") {
    return { label: "Arrival confirmed", done: true };
  }
  if (raw !== "accepted") return null;
  return { label: "Confirm Arrival", done: false };
};

// Driven by the booking's real status, not the date-derived display label: the
// buttons complete a two-party handshake on the server, so what they offer has
// to match what the server will accept.
const getPrimaryAction = (trip) => {
  const raw = String(trip?.rawStatus || "").trim().toLowerCase();
  const confirmations = trip?.rawItem?.confirmations || {};

  if (raw === "accepted" || raw === "assistant_arrived") {
    return confirmations.start?.assistant
      ? {
          label: "Waiting for traveller to confirm start",
          phase: "start",
          done: true,
          hint: "Their confirmation starts the trip and releases the first half of your fee.",
        }
      : {
          label: "Confirm Trip Started",
          phase: "start",
          done: false,
          hint: "Step 1 of 2. The trip has to be started before it can be ended, even if it has already run.",
        };
  }

  if (raw === "trip_started") {
    return confirmations.end?.assistant
      ? {
          label: "Waiting for traveller to confirm end",
          phase: "end",
          done: true,
          hint: "Their confirmation closes the trip and releases the rest of your fee.",
        }
      : {
          label: "Confirm Trip Ended",
          phase: "end",
          done: false,
          hint: "Step 2 of 2. Releases the rest of your fee once the traveller confirms too.",
        };
  }

  return null;
};

export default function Tours() {
  const navigate = useNavigate();
  const toast = useToast();
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(false);
  // Tracks *that* the load failed so the table can say so; the reason itself is
  // toasted, not printed into the row.
  const [loadFailed, setLoadFailed] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedFilter, setSelectedFilter] = useState("Most Recent");
  const [selectedTrip, setSelectedTrip] = useState(null);
  const [tripActionLoading, setTripActionLoading] = useState(false);
  const [arrivalLoading, setArrivalLoading] = useState(false);
  const [tripDetail, setTripDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => {
    let active = true;

    const fetchTrips = async () => {
      setLoading(true);
      setLoadFailed(false);

      try {
        const response = await bookingService.getBookings({ page: 1, limit: 100 });
        if (!active) return;
        setTrips(normalizeTripsPayload(response));
      } catch (err) {
        if (!active) return;
        setLoadFailed(true);
        setTrips([]);
        toast.apiError(err, "Unable to load trips right now.");
      } finally {
        if (active) setLoading(false);
      }
    };

    fetchTrips();
    return () => {
      active = false;
    };
  }, [toast]);

  const sortedTrips = useMemo(() => {
    return [...trips].sort((a, b) => {
      const dateA = parseDateValue(a.date)?.getTime() || 0;
      const dateB = parseDateValue(b.date)?.getTime() || 0;
      return dateB - dateA;
    });
  }, [trips]);

  const visibleTrips = useMemo(() => {
    if (selectedFilter === "Most Recent") {
      return sortedTrips;
    }

    return sortedTrips.filter((trip) => trip.status === selectedFilter);
  }, [selectedFilter, sortedTrips]);

  const totalPages = Math.max(1, Math.ceil(visibleTrips.length / PAGE_SIZE));
  const safePage = Math.min(currentPage, totalPages);
  const pagedTrips = useMemo(
    () =>
      visibleTrips.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE),
    [safePage, visibleTrips]
  );

  useEffect(() => {
    setCurrentPage((prev) => Math.min(prev, totalPages));
  }, [totalPages]);

  const handleViewTrip = async (trip) => {
    setSelectedTrip(trip);
    setTripDetail(normalizeTripDetail({}, trip));
    setDetailLoading(Boolean(trip?.id));

    if (!trip?.id) {
      setDetailLoading(false);
      return;
    }

    try {
      const response = await bookingService.getBookingById(trip.id);
      setTripDetail(normalizeTripDetail(response, trip));
    } catch (err) {
      toast.apiError(err, "Unable to load trip details right now.");
    } finally {
      setDetailLoading(false);
    }
  };

  const closeTripDetails = () => {
    setSelectedTrip(null);
    setTripDetail(null);
    setDetailLoading(false);
  };

  // Re-normalises from the booking the server returned so the row, the drawer
  // and the action button all agree on the new state.
  const applyTripUpdate = (bookingId, updatedBooking) => {
    const merge = (trip) => {
      const rawItem = { ...(trip.rawItem || {}), ...updatedBooking };
      return {
        ...trip,
        rawItem,
        rawStatus: updatedBooking.status,
        status: mapTripStatus(updatedBooking.status, trip.date, trip.checkOut),
      };
    };
    const matches = (trip) => {
      const id = trip?.rawItem?._id || trip?.rawItem?.id;
      return String(id) === String(bookingId);
    };

    setTrips((prev) => prev.map((trip) => (matches(trip) ? merge(trip) : trip)));
    setSelectedTrip((prev) => (prev && matches(prev) ? merge(prev) : prev));
    setTripDetail((prev) =>
      prev
        ? {
            ...prev,
            status: mapTripStatus(
              updatedBooking.status,
              prev.date,
              prev.checkOut
            ),
          }
        : prev
    );
  };

  const handleConfirmArrival = async () => {
    const action = getArrivalAction(selectedTrip);
    const bookingId = selectedTrip?.rawItem?._id || selectedTrip?.rawItem?.id;
    if (!action || action.done || !bookingId) return;

    setArrivalLoading(true);
    try {
      const response = await bookingService.assistantArrival(bookingId);
      const updated = response?.data?.booking || response?.booking;
      if (updated?.status) {
        applyTripUpdate(bookingId, updated);
        toast.success("Arrival confirmed. The traveller has been notified.");
      }
    } catch (err) {
      toast.apiError(err, "Unable to confirm your arrival right now.");
    } finally {
      setArrivalLoading(false);
    }
  };

  const handlePrimaryTripAction = async () => {
    const action = getPrimaryAction(selectedTrip);
    const bookingId = selectedTrip?.rawItem?._id || selectedTrip?.rawItem?.id;
    if (!action || action.done || !bookingId) return;

    setTripActionLoading(true);
    try {
      const response =
        action.phase === "start"
          ? await bookingService.startTrip(bookingId)
          : await bookingService.completeTrip(bookingId);

      // Only the server knows whether the traveller has confirmed too, so the
      // returned booking decides the new state — never assume it advanced.
      const updated = response?.data?.booking || response?.booking;
      if (updated?.status) {
        applyTripUpdate(bookingId, updated);
        const advanced =
          action.phase === "start"
            ? updated.status === "trip_started"
            : updated.status === "completed";
        toast.success(
          advanced
            ? action.phase === "start"
              ? "Trip started."
              : "Trip completed."
            : "Confirmed. Waiting for the traveller to confirm."
        );
      }
    } catch (err) {
      toast.apiError(err, "Unable to update this trip right now.");
    } finally {
      setTripActionLoading(false);
    }
  };

  const handleMessageClient = () => {
    closeTripDetails();
    navigate("/dashboards/assistant/messages");
  };

  return (
    <>
      <div className="space-y-4">
      <div className="rounded-xl border border-[#EEF0F3] bg-white">
        <div className="flex items-center justify-between border-b px-4 py-4">
          <h1 className="text-2xl font-semibold text-[#1D2939]">Trips</h1>

          <AppSelect
            value={selectedFilter}
            onChange={(value) => {
              setSelectedFilter(value);
              setCurrentPage(1);
            }}
            options={FILTER_OPTIONS}
            buttonPrefix="Sort by:"
            containerClassName="w-fit"
            buttonClassName="!w-auto !min-w-[180px] !justify-end !gap-1 !border-0 !bg-transparent !px-0 !py-0 !text-sm !font-medium !text-gray-500 !shadow-none focus:!ring-0 focus:!border-transparent"
            optionsClassName="!left-auto !right-0 !mt-2 !w-[200px] !max-h-[240px] !rounded-2xl !border-[#D0D5DD] !py-1.5"
            optionClassName="!px-4 !py-3 !text-base !font-medium !text-[#344054]"
            showCheckIcon={false}
          />
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-[760px] w-full text-base">
            <thead className="border-b text-left">
              <tr>
                <th className="px-4 py-3 text-sm font-semibold uppercase tracking-[0.06em] text-gray-500">
                  Location
                </th>
                <th className="px-4 py-3 text-sm font-semibold uppercase tracking-[0.06em] text-gray-500">
                  Date
                </th>
                <th className="px-4 py-3 text-sm font-semibold uppercase tracking-[0.06em] text-gray-500">
                  No of Days
                </th>
                <th className="px-4 py-3 text-sm font-semibold uppercase tracking-[0.06em] text-gray-500">
                  Participants
                </th>
                <th className="px-4 py-3 text-sm font-semibold uppercase tracking-[0.06em] text-gray-500">
                  Amount
                </th>
                <th className="px-4 py-3 text-sm font-semibold uppercase tracking-[0.06em] text-gray-500">
                  Status
                </th>
              </tr>
            </thead>

            <tbody className="divide-y">
              {loading ? (
                <tr>
                  <td
                    colSpan={6}
                    className="px-4 py-8 text-center text-base font-medium text-gray-500"
                  >
                    Loading trips...
                  </td>
                </tr>
              ) : loadFailed ? (
                <tr>
                  <td
                    colSpan={6}
                    className="px-4 py-8 text-center text-base font-medium text-[#667085]"
                  >
                    Couldn't load trips.
                  </td>
                </tr>
              ) : pagedTrips.length === 0 ? (
                <tr>
                  <td
                    colSpan={6}
                    className="px-4 py-8 text-center text-base font-medium text-gray-500"
                  >
                    No trips found.
                  </td>
                </tr>
              ) : (
                pagedTrips.map((trip) => (
                  <tr
                    key={trip.id}
                    onClick={() => handleViewTrip(trip)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        handleViewTrip(trip);
                      }
                    }}
                    className="cursor-pointer transition hover:bg-[#F9FAFB]"
                    tabIndex={0}
                    role="button"
                  >
                    <td className="px-4 py-3 text-base font-medium text-[#344054]">
                      {trip.location}
                    </td>
                    <td className="px-4 py-3 text-base font-medium text-[#475467]">
                      {trip.dateLabel}
                    </td>
                    <td className="px-4 py-3 text-base font-medium text-[#475467]">
                      {trip.numberOfDays}
                    </td>
                    <td className="px-4 py-3 text-base font-medium text-[#475467]">
                      {trip.participants}
                    </td>
                    <td className="px-4 py-3 text-base font-medium text-[#475467]">
                      {trip.amountLabel}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex rounded-full px-3 py-1.5 text-sm font-semibold ${statusStyles[trip.status]}`}
                      >
                        {trip.status}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between px-4 py-4 text-base">
          <p className="font-medium text-gray-500">
            Showing {pagedTrips.length ? (safePage - 1) * PAGE_SIZE + 1 : 0} to{" "}
            {(safePage - 1) * PAGE_SIZE + pagedTrips.length} of {visibleTrips.length}{" "}
            results
          </p>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
              disabled={safePage === 1}
              className="rounded-full p-1 text-gray-500 transition hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-40"
              aria-label="Previous page"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>

            {Array.from({ length: totalPages }, (_, index) => index + 1).map((page) => (
              <button
                key={page}
                type="button"
                onClick={() => setCurrentPage(page)}
                className={`flex h-8 w-8 items-center justify-center rounded-full text-sm font-medium transition ${
                  page === safePage
                    ? "bg-[#F2F4F7] text-[#344054]"
                    : "text-[#667085] hover:bg-gray-100"
                }`}
              >
                {page}
              </button>
            ))}

            <button
              type="button"
              onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
              disabled={safePage === totalPages}
              className="rounded-full p-1 text-gray-500 transition hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-40"
              aria-label="Next page"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
      </div>

      {selectedTrip ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-[20px] bg-white shadow-2xl">
            <div className="px-5 pb-5 pt-4">
              <button
                type="button"
                onClick={closeTripDetails}
                className="mb-3 inline-flex rounded-full p-1.5 text-[#101828] transition hover:bg-[#F2F4F7]"
                aria-label="Back"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>

              <div className="space-y-5">
                <h2 className="text-base font-semibold text-[#101828]">
                  Booking Details
                </h2>

                {detailLoading ? (
                  <p className="text-sm font-medium text-[#667085]">
                    Loading trip details...
                  </p>
                ) : (
                  <>
                    <div className="space-y-3">
                      <TripDetailRow
                        icon={User}
                        label="Name"
                        value={tripDetail?.customerName || "—"}
                      />
                      <TripDetailRow
                        icon={Users}
                        label="Participants"
                        value={tripDetail?.participants || "—"}
                      />
                      <TripDetailRow
                        icon={MapPin}
                        label="Location"
                        value={tripDetail?.location || "—"}
                      />
                      <TripDetailRow
                        icon={CalendarDays}
                        label="Check-in"
                        value={tripDetail?.checkInLabel || "—"}
                      />
                      <TripDetailRow
                        icon={CalendarDays}
                        label="Check-out"
                        value={tripDetail?.checkOutLabel || "—"}
                      />
                      <TripDetailRow
                        icon={Clock}
                        label="Duration"
                        value={tripDetail?.durationLabel || "—"}
                      />
                      <TripDetailRow
                        icon={Receipt}
                        label="Reference"
                        value={tripDetail?.reference || "—"}
                      />
                      <TripDetailRow
                        icon={CalendarDays}
                        label="Booked on"
                        value={tripDetail?.bookedOnLabel || "—"}
                      />
                    </div>

                    <TripDetailTextBlock
                      icon={ClipboardList}
                      label="Itinerary"
                      value={tripDetail?.itinerary || "—"}
                    />

                    <div className="space-y-3">
                      <h3 className="text-base font-semibold text-[#101828]">
                        Logistics
                      </h3>
                      <TripDetailRow
                        icon={Shield}
                        label="Security Officers"
                        value={tripDetail?.securityOfficersLabel || "—"}
                      />
                      <TripDetailRow
                        icon={CarFront}
                        label="Convoy Vehicles"
                        value={tripDetail?.convoyVehiclesLabel || "—"}
                      />
                    </div>

                    <div className="space-y-3">
                      <h3 className="text-base font-semibold text-[#101828]">
                        Payment Summary
                      </h3>
                      <TripDetailRow
                        label="Base Trip Cost"
                        value={tripDetail?.baseTripCostLabel || "₦0.00"}
                      />
                      <TripDetailRow
                        label="Vehicle Cost"
                        value={tripDetail?.vehicleCostLabel || "₦0.00"}
                      />
                      <TripDetailRow
                        label="Security Cost"
                        value={tripDetail?.securityCostLabel || "₦0.00"}
                      />
                      <TripDetailRow
                        label="Total"
                        value={tripDetail?.totalLabel || "₦0.00"}
                      />
                    </div>
                  </>
                )}

                {!detailLoading ? (
                  <div className="space-y-2">
                    {getArrivalAction(selectedTrip) ? (
                      <button
                        type="button"
                        onClick={handleConfirmArrival}
                        disabled={
                          arrivalLoading || getArrivalAction(selectedTrip).done
                        }
                        className="w-full rounded-md border border-[#C97AAA] px-4 py-3 text-sm font-semibold text-[#7A054A] transition hover:bg-[#F8EEF4] disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        {arrivalLoading
                          ? "Confirming..."
                          : getArrivalAction(selectedTrip).label}
                      </button>
                    ) : null}

                    {getPrimaryAction(selectedTrip) ? (
                      <button
                        type="button"
                        onClick={handlePrimaryTripAction}
                        disabled={
                          tripActionLoading || getPrimaryAction(selectedTrip).done
                        }
                        className="w-full rounded-md bg-[#61013B] px-4 py-3 text-sm font-semibold text-white transition hover:bg-[#450029] disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        {tripActionLoading
                          ? "Confirming..."
                          : getPrimaryAction(selectedTrip).label}
                      </button>
                    ) : null}

                    {getPrimaryAction(selectedTrip)?.hint ? (
                      <p className="text-xs text-[#667085]">
                        {getPrimaryAction(selectedTrip).hint}
                      </p>
                    ) : null}

                    <button
                      type="button"
                      onClick={handleMessageClient}
                      className="w-full rounded-md border border-[#C97AAA] px-4 py-3 text-sm font-semibold text-[#7A054A] transition hover:bg-[#F8EEF4]"
                    >
                      Message Client
                    </button>
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}

function TripDetailRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start justify-between gap-4 text-xs">
      <div className="flex items-center gap-2 text-[#667085]">
        {Icon ? <Icon className="h-3.5 w-3.5 shrink-0" /> : null}
        <span>{label}</span>
      </div>
      <span className="max-w-[50%] text-right font-medium text-[#7A054A]">
        {value || "—"}
      </span>
    </div>
  );
}

function TripDetailTextBlock({ icon: Icon, label, value }) {
  if (!value) return null;

  return (
    <div className="space-y-2 rounded-xl border border-[#EEF0F3] bg-[#F9FAFB] px-3 py-3">
      <div className="flex items-center gap-2 text-xs font-semibold text-[#667085]">
        <Icon className="h-3.5 w-3.5 shrink-0" />
        <span>{label}</span>
      </div>
      <p className="whitespace-pre-wrap text-xs font-medium leading-5 text-[#7A054A]">
        {value}
      </p>
    </div>
  );
}
